"use client";

import { useCallback, useEffect, useState, type ComponentType } from "react";
import { MessageCircle, X } from "lucide-react";
import type { SupportPanelProps } from "./SupportPanel";
import { lockScroll } from "@/lib/scrollLock";

/**
 * The floating chat button on every page of the website and the dashboard
 * (mounted once in the root layout). The chat itself (SupportPanel) is only
 * downloaded the first time someone hovers or opens it, so pages stay fast.
 *
 * Open it from anywhere with openSupport() — optionally with a first message:
 *   openSupport("I can't add my website")
 * or with a link to any page + ?support=open (used in support reply emails).
 */

export const SUPPORT_OPEN_EVENT = "rb:support-open";

export function openSupport(message?: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(SUPPORT_OPEN_EVENT, { detail: { message } }));
}

const HIDE_ON = [/^\/r\//, /^\/offline/];

export function SupportLauncher() {
  const [open, setOpen] = useState(false);
  const [Panel, setPanel] = useState<ComponentType<SupportPanelProps> | null>(null);
  const [unread, setUnread] = useState(0);
  const [initial, setInitial] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(() => {
    if (Panel) return;
    import("./SupportPanel")
      .then((m) => setPanel(() => m.SupportPanel))
      .catch(() => {
        /* offline or an old copy of the app: the button still opens the contact page */
      });
  }, [Panel]);

  const show = useCallback(
    (message?: string) => {
      load();
      setOpen(true);
      setUnread(0);
      if (message) setInitial(message);
    },
    [load],
  );

  useEffect(() => {
    setHidden(HIDE_ON.some((r) => r.test(window.location.pathname)));
    const onOpen = (e: Event) => show((e as CustomEvent<{ message?: string }>).detail?.message);
    window.addEventListener(SUPPORT_OPEN_EVENT, onOpen);
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("support") === "open") {
        show();
        url.searchParams.delete("support");
        window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
      }
    } catch {
      /* ignore */
    }
    return () => window.removeEventListener(SUPPORT_OPEN_EVENT, onOpen);
  }, [show]);

  // a phone's full-screen chat shouldn't scroll the page behind it
  useEffect(() => {
    if (!open || window.innerWidth >= 640) return;
    return lockScroll();
  }, [open]);

  const onUnread = useCallback((n: number) => setUnread((u) => u + n), []);
  const close = useCallback(() => setOpen(false), []);
  const used = useCallback(() => setInitial(null), []);

  if (hidden) return null;

  return (
    <>
      {Panel && <Panel open={open} onClose={close} onUnread={onUnread} initial={initial} onInitialUsed={used} />}
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : show())}
        onMouseEnter={load}
        onFocus={load}
        onTouchStart={load}
        aria-label={open ? "Close the chat" : "Chat with Rankbist support"}
        aria-expanded={open}
        className={`rb-support-launcher group fixed bottom-5 right-4 z-[110] flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#8f78ff] via-[#6a52ff] to-[#4b33e0] text-white shadow-[0_16px_34px_-10px_rgba(75,51,224,0.75),inset_0_1px_0_rgba(255,255,255,0.3)] ring-4 ring-white/70 transition-[transform,opacity] duration-300 hover:scale-105 active:scale-95 sm:bottom-6 sm:right-6 print:hidden ${
          open ? "max-sm:pointer-events-none max-sm:scale-75 max-sm:opacity-0" : ""
        }`}
      >
        {!open && <span aria-hidden className="rb-support-ping absolute inset-0 rounded-full bg-[#8f78ff]" />}
        <span className="relative">
          {open ? <X size={24} strokeWidth={2.4} /> : <MessageCircle size={25} strokeWidth={2.2} className="transition-transform duration-300 group-hover:-rotate-6" />}
        </span>
        {unread > 0 && !open && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#ef4444] px-1 text-[11px] font-extrabold text-white ring-2 ring-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
    </>
  );
}
